/**
 * Input — headless proprietary primitive.
 *
 * Client component (consumes `PermissionContext` + `AuditContext` via
 * `useContext`). Wraps the native `<input>` with:
 *   - Permission gating via `can('edit', resource, attrs)`. The three
 *     modes collapse cleanly for form fields: `disabled` → native
 *     disabled, `readOnly` → native readOnly (visible, focus-reachable,
 *     non-mutating), `hidden` → returns null.
 *   - Canonical `input:change` audit emission on blur (not per-keystroke —
 *     that would drown the stream; blur is the semantic "I'm done typing"
 *     boundary). Also `input:blur` when a consumer needs the signal
 *     separately. `permission:denied` on blocked edits in `readOnly`.
 *   - `data-oshon-primitive="input"` + `data-permission-state` styling hooks.
 *
 * Proprietary rather than Radix-wrapped because native `<input>` behavior
 * is narrow — label wiring (htmlFor), form association, constraint
 * validation, ARIA defaults all come free. See ADR-001 for the
 * "proprietary vs. Radix" rubric (same rubric applied to Button).
 */

'use client';

import { forwardRef, useRef } from 'react';
import type {
  FocusEventHandler,
  ChangeEventHandler,
  InputHTMLAttributes,
  Ref,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'disabled' | 'readOnly' | 'aria-invalid'
>;

export interface InputProps extends NativeInputProps {
  /**
   * Disable the input. Renders with the native `disabled` attribute so
   * screen readers and keyboard users get consistent signal. No change /
   * blur audit events fire while disabled.
   */
  disabled?: boolean;
  /**
   * Non-mutating but still interactive. Renders with native `readOnly`
   * so the field is focus-reachable, copy-friendly, and announced as
   * read-only by assistive tech. Change events are silently dropped.
   */
  readOnly?: boolean;
  /**
   * Marks the field as invalid for a11y. `true` → `aria-invalid="true"`.
   * The visual layer surfaces the error styling; primitive only ensures
   * correct ARIA.
   */
  invalid?: boolean;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. Example: `permissions={{ mode: 'hidden' }}`
   * switches this one instance to render-as-null when denied.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name passed to `permissions.can('edit', resource, attrs)`.
   * Default `'input'`. Consumers override for specific semantics, e.g.
   * `resource="field:ssn"` for a sensitive data field.
   */
  resource?: string;
  /**
   * Attribute bag forwarded to `permissions.can(..., attrs)` for
   * attribute-based access control. Primitives never invent values here
   * — the consumer supplies them.
   */
  permissionAttrs?: Record<string, unknown>;
}

/**
 * Headless input. Accessible `<input>` with:
 *   - `disabled` / `readOnly` / `aria-invalid` / `aria-disabled`
 *     synchronised
 *   - Permission gating via `can('edit', resource, attrs)`:
 *       - `mode='disabled'` (default) — renders native `disabled`
 *       - `mode='readOnly'` — renders native `readOnly` (visible,
 *         focus-reachable, non-mutating)
 *       - `mode='hidden'` — returns null
 *   - Canonical `input:change` + `input:blur` audit on blur (granted);
 *     `permission:denied` when the user attempts to mutate a
 *     readOnly-gated field
 *   - `data-oshon-primitive="input"` styling hook
 *   - `data-permission-state="denied"` styling hook under any deny mode
 *     that keeps the affordance visible
 *   - No visual styling — consumers supply their own className / styles.
 *
 * Keyboard: native browser behavior — Tab focuses, typing mutates.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      disabled = false,
      readOnly = false,
      invalid = false,
      permissions,
      resource = 'input',
      permissionAttrs,
      onChange,
      onBlur,
      value,
      defaultValue,
      ...rest
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) {
    const contextPermissions = usePermissions();
    const effective = resolvePermissions(permissions, contextPermissions);
    const mode = effective.mode ?? 'disabled';
    const emit = useAudit();

    const { granted, denyReason } = evaluatePermission(
      effective,
      'edit',
      resource,
      permissionAttrs,
    );

    // Track whether the current value differs from the value at focus so
    // the blur emit only fires `input:change` when the user actually
    // changed something. Consumers who want per-keystroke telemetry layer
    // that on top via `onChange` — primitive stream stays quiet.
    const focusValueRef = useRef<string | null>(null);
    const changedSinceFocusRef = useRef(false);

    if (!granted && mode === 'hidden') return null;

    const permissionBlocks = !granted && mode !== 'readOnly';
    const isDisabled = disabled || permissionBlocks;
    const isReadOnly = readOnly || (!granted && mode === 'readOnly');

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      if (disabled) return;

      if (!granted && mode === 'readOnly') {
        // User attempted to mutate a permission-gated readOnly field.
        // Record the denial once per keystroke attempt — governance can
        // aggregate these into "user tried to edit X N times" signals.
        emit({
          component: 'input',
          action: CANONICAL_ACTIONS.PERMISSION_DENIED,
          permissionResult: 'denied',
          ...(denyReason ? { denyReason } : {}),
          details: { attemptedAction: 'edit', resource },
        });
        // Don't forward — a readOnly field must not let consumer state
        // drift into an edit it rejected.
        return;
      }

      if (readOnly) {
        // Locally readOnly — silent drop, no audit (the consumer
        // declared the field read-only on purpose).
        return;
      }

      changedSinceFocusRef.current = true;
      onChange?.(event);
    };

    const handleFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      focusValueRef.current = event.currentTarget.value;
      changedSinceFocusRef.current = false;
      rest.onFocus?.(event);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
      // Blur fires regardless of permission state — disabled fields
      // can't be focused, so a blur here implies the field was
      // interactable. Still audit even if the user didn't change
      // anything (consumers can filter on `details.changed`).
      if (!disabled && granted && !readOnly) {
        emit({
          component: 'input',
          action: CANONICAL_ACTIONS.INPUT_BLUR,
          permissionResult: 'granted',
          details: { changed: changedSinceFocusRef.current },
        });
        if (changedSinceFocusRef.current) {
          emit({
            component: 'input',
            action: CANONICAL_ACTIONS.INPUT_CHANGE,
            permissionResult: 'granted',
          });
        }
      }
      onBlur?.(event);
    };

    const permissionDataState =
      !granted && mode !== 'hidden' ? 'denied' : undefined;

    const resolvedTitle =
      rest.title ?? (permissionDataState && denyReason ? denyReason : undefined);

    return (
      <input
        {...rest}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        disabled={isDisabled}
        readOnly={isReadOnly}
        aria-disabled={isDisabled || undefined}
        aria-readonly={isReadOnly || undefined}
        aria-invalid={invalid || undefined}
        data-oshon-primitive="input"
        data-permission-state={permissionDataState}
        title={resolvedTitle}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  },
);
